import {
    type FC,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { useDrag } from "react-dnd";
import FontAwesomeSolid from "../../../../Utilities/Platform/FontAwesome/FontAwesomeSolid";
import { type GridDragItemFactory, type GridDragItemType } from "../GridDropLayer";
import { GRID_CURSOR_DEPTH, GRID_DROP_DEPTH, type GridValidCallback } from "../GridEditor";
import { GRID_CURSOR_MOVE_TYPE, GridCursorDirection, GridCursorHandle } from "./GridCursorHandle";

export interface GridCursorType {
    top: number;
    left: number;
    width: number;
    height: number;
}

interface Props {
    selected: string;
    valid: boolean;
    onValid: undefined | GridValidCallback;
    cursor: GridCursorType;
    onEnd: (dropped: boolean) => void;
    onDrop: (cursor: GridCursorType) => void;
    onDelete: (id: string) => void;
}

export const GridCursor: FC<Props> = ({
    valid,
    onValid,
    cursor,
    selected,
    onEnd,
    onDrop,
    onDelete,
}) => {
    const anchorRef = useRef<HTMLDivElement>(null);

    const validRef = useRef(valid);
    const cursorRef = useRef(cursor);
    const deletingRef = useRef(false);
    const deletingTimeout = useRef<ReturnType<typeof setTimeout>>(null);
    const selectedRef = useRef(selected);

    const [ deleting, setDeleting ] = useState(false);
    const [ editCursor, setEditCursor ] = useState({ ...cursor });

    // Validate the cursor every time the cursor is moved
    useEffect(() => {
        if(!editCursor || typeof onValid !== "function" || !selected) {
            return;
        }

        onValid(selected, editCursor);
    }, [ editCursor, onValid, selected ]);

    // Reset deleting when the selected item changes
    useEffect(() => {
        if(deletingTimeout.current) {
            clearTimeout(deletingTimeout.current);
        }

        setDeleting(false);
    }, [ selected ]);

    useEffect(() => {
        setEditCursor(cursor);
    }, [ cursor ]);

    // Setting the refs (this method is quicker than using useEffect)
    cursorRef.current = editCursor;
    validRef.current = valid;
    deletingRef.current = deleting;
    selectedRef.current = selected;

    useEffect(() => {
        const listener = (e: KeyboardEvent) => {
            if(e.target instanceof HTMLInputElement) {
                return;
            }

            if(e.key !== "Delete") {
                return;
            }

            if(deletingRef.current) {
                if(deletingTimeout.current) {
                    clearTimeout(deletingTimeout.current);
                }

                onDelete(selectedRef.current);
                setDeleting(false);

                return;
            }

            setDeleting(true);
            deletingTimeout.current = setTimeout(() => {
                setDeleting(false);
            }, 1000);
        };

        window.addEventListener("keydown", listener);

        return () => {
            if(deletingTimeout.current) {
                clearTimeout(deletingTimeout.current);
            }

            window.removeEventListener("keydown", listener);
        };
    }, [ selected, onDelete ]);

    const createItem = useCallback<GridDragItemFactory>(() => {
        if(!anchorRef.current) {
            return null;
        }

        const anchorBounding = anchorRef.current.getBoundingClientRect();

        return {
            selected,
            cursor   : cursorRef,
            setCursor: setEditCursor,
            anchor   : {
                top : anchorBounding.top,
                left: anchorBounding.left,
            },
        };
    }, [ selected ]);

    // Handles the drop logic here
    const onDragEnd = useCallback((item: GridDragItemType) => {
        const cursor = item.cursor.current;

        if(!validRef.current || !cursor) {
            onEnd(false);

            return;
        }

        onDrop(cursor);
        onEnd(true);
    }, [ onDrop, onEnd ]);

    const [ , drag ] = useDrag(() => ({
        type: GRID_CURSOR_MOVE_TYPE,
        item: createItem,
        end : onDragEnd,
    }), [ createItem, onDragEnd ]);

    return (
        <>
            <div
                ref={anchorRef}
                className="grid-cursor relative"
                style={{
                    // Anchor
                    gridColumnStart: editCursor.left,
                    gridColumnEnd  : `span ${editCursor.width}`,
                    gridRowStart   : editCursor.top,
                    gridRowEnd     : `span ${editCursor.height}`,
                    zIndex         : GRID_CURSOR_DEPTH,
                }}
            >
                <div
                    ref={el => {
                        drag(el);
                    }}
                    className={
                        "grid-cursor cursor-move absolute inset-0 border-4 border-solid " + (
                            (valid && !deleting) ? "border-blue-500" : "border-red-500"
                        )
                    }
                />
                <GridCursorHandle
                    onEnd={onDragEnd}
                    valid={valid}
                    deleting={deleting}
                    className="-left-3 -top-3"
                    direction={GridCursorDirection.LEFT | GridCursorDirection.TOP}
                    createItem={createItem}
                />
                <GridCursorHandle
                    onEnd={onDragEnd}
                    valid={valid}
                    deleting={deleting}
                    className="left-1/2 -top-3 -ml-2"
                    direction={GridCursorDirection.TOP}
                    createItem={createItem}
                />
                <GridCursorHandle
                    onEnd={onDragEnd}
                    valid={valid}
                    deleting={deleting}
                    className="-right-3 -top-3"
                    direction={GridCursorDirection.RIGHT | GridCursorDirection.TOP}
                    createItem={createItem}
                />
                <GridCursorHandle
                    onEnd={onDragEnd}
                    valid={valid}
                    deleting={deleting}
                    className="-right-3 top-1/2 -mt-2"
                    direction={GridCursorDirection.RIGHT}
                    createItem={createItem}
                />
                <GridCursorHandle
                    onEnd={onDragEnd}
                    valid={valid}
                    deleting={deleting}
                    className="-right-3 -bottom-3"
                    direction={GridCursorDirection.RIGHT | GridCursorDirection.BOTTOM}
                    createItem={createItem}
                />
                <GridCursorHandle
                    onEnd={onDragEnd}
                    valid={valid}
                    deleting={deleting}
                    className="left-1/2 -bottom-3 -ml-2"
                    direction={GridCursorDirection.BOTTOM}
                    createItem={createItem}
                />
                <GridCursorHandle
                    onEnd={onDragEnd}
                    valid={valid}
                    deleting={deleting}
                    className="-left-3 -bottom-3"
                    direction={GridCursorDirection.BOTTOM | GridCursorDirection.LEFT}
                    createItem={createItem}
                />
                <GridCursorHandle
                    onEnd={onDragEnd}
                    valid={valid}
                    deleting={deleting}
                    className="-left-3 top-1/2 -mt-2"
                    direction={GridCursorDirection.LEFT}
                    createItem={createItem}
                />
            </div>
            {deleting && (
                <div
                    className="grid-cursor pointer-events-none flex justify-center items-center"
                    style={{
                        // Anchor
                        gridColumnStart: editCursor.left,
                        gridColumnEnd  : `span ${editCursor.width}`,
                        gridRowStart   : editCursor.top,
                        gridRowEnd     : `span ${editCursor.height}`,
                        zIndex         : GRID_DROP_DEPTH,
                    }}
                >
                    <div
                        className={
                            "flex justify-center items-center w-12 h-12 rounded-full backdrop-blur-xs " +
                            "bg-neutral-800/70 text-red-300 text-3xl"
                        }
                    >
                        <FontAwesomeSolid icon="trash-alt" />
                    </div>
                </div>
            )}
        </>
    );
};
